import { AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAccountRestrictions } from '@/hooks/use-account-restrictions';
import { useUnpaidInvoices } from '@/hooks/use-unpaid-invoices';
import { useCustomerPortal } from '@/app/billing/hooks/use-billing-mutations';
import { brandingConfig } from '@/config/branding';

function formatAmount(amount: number, currency: string) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
    }).format(amount / 100);
}

export function AccountWarningBanner() {
    const { showWarningBanner, hasAccessRestriction } = useAccountRestrictions();
    const { invoiceCount, totalAmount, currency, isLoading } = useUnpaidInvoices(showWarningBanner);
    const portalMutation = useCustomerPortal();

    if (!showWarningBanner || isLoading || invoiceCount === 0) {
        return null;
    }

    const isBlocking = hasAccessRestriction;
    const Icon = isBlocking ? XCircle : AlertTriangle;
    const bgColor = isBlocking ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200';
    const iconColor = isBlocking ? 'text-red-600' : 'text-amber-600';
    const titleColor = isBlocking ? 'text-red-900' : 'text-amber-900';
    const textColor = isBlocking ? 'text-red-700' : 'text-amber-700';

    return (
        <div className={`${bgColor} border-b px-4 py-3`}>
            <div className="mx-auto flex max-w-screen-2xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3 sm:items-center">
                    <Icon className={`h-5 w-5 ${iconColor} mt-0.5 flex-shrink-0 sm:mt-0`} />
                    <div className="min-w-0">
                        <p className={`text-sm font-semibold ${titleColor}`}>
                            {invoiceCount} unpaid invoice{invoiceCount > 1 ? 's' : ''} — {formatAmount(totalAmount, currency)}
                        </p>
                        <p className={`text-xs ${textColor}`}>
                            {isBlocking
                                ? 'Your account access is restricted until all invoices are paid.'
                                : 'Please pay outstanding invoices to avoid account restrictions.'}
                            {brandingConfig.supportEmail && (
                                <>
                                    {' '}If you think this is an error, contact us at{' '}
                                    <a href={`mailto:${brandingConfig.supportEmail}`} className="underline">{brandingConfig.supportEmail}</a>
                                </>
                            )}
                        </p>
                    </div>
                </div>
                <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => portalMutation.mutate({ returnUrl: window.location.href })}
                    disabled={portalMutation.isPending}
                    className="w-full shrink-0 sm:w-auto"
                >
                    {portalMutation.isPending ? 'Opening...' : 'Pay Now'}
                </Button>
            </div>
        </div>
    );
}
